import * as LS from 'local-storage'
import { CONSENT_SOURCE, DISCOUNT_CODE_TYPE } from 'types'
import calculatePaymentAmount from './calculatePaymentAmount'
import convertDollarsToCents from './convertDollarsToCents'
import { mapKeys } from 'lodash'

// Builds the payload for the unified purchase API. Used for both new 
// memberships and renewals.
const apiValuesForPurchase = ({
  account,
  billing,
  contact,
  discountCodeDetails,
  gclid,
  isTrial,
  membership,
  membershipProducts,
  referralInfo,
  referredByMemberNumber,
  selectedMembershipType,
  selectedTrailerCareType,
  testVariant,
  trailerCareProducts,
}) => {
  // Account
  const { heroku_external_id__c: herokuAccountId } = account
  const {
    billingstreet,
    billingcity,
    billingstate,
    billingpostalcode,
    billingcountry,
  } = account

  // Contact
  const contactEmail = contact?.email
  const contactFirstName = contact?.firstname
  const contactLastName = contact?.lastname

  // Membership
  const {
    heroku_external_id__c: herokuMembershipId,
    membership_contact__r__heroku_external_id__c: membershipContactId,
    shopping_cart_membership_type__c,
    shopping_cart_trailer_care_type__c,
  } = membership

  // Billing & Payment Totals
  const {
    discountedMembershipCost,
    finalDonation: donation_amount,
    referralCreditsUsed,
    subtotalWithoutDonations: subtotal,
    stripePaymentIntentId,
    stripePaymentMethodId,
    stripeSubscriptionId,
  } = billing

  const totalCost = calculatePaymentAmount({
    discountedMembershipCost,
    referralCreditsUsed,
    subtotal,
    donationAmount: donation_amount,
  })
  const totalAmountInCents = convertDollarsToCents(totalCost)

  // Core Payload
  let payload = {
    stripe_payment_intent_id: stripePaymentIntentId,
    stripe_payment_method_id: stripePaymentMethodId,
    stripe_subscription_id: stripeSubscriptionId,
    amount: totalAmountInCents,
    total_cost: totalCost,
    donation_amount: parseFloat(donation_amount),
    isRecurring: billing.autoRenewalAgreement ?? false,
    is_trial: isTrial ?? false,
    sms_opt_in__c: true,
    firstName: contactFirstName,
    lastName: contactLastName,
    shopping_cart_membership_type__c,
    shopping_cart_trailer_care_type__c,

    cc_details: {
      billingaddress: {
        billingstreet,
        billingcity,
        billingstate,
        billingpostalcode,
        billingcountry,
      },
      auto_renewal_agreement: billing.autoRenewalAgreement || false,
      first_name: contactFirstName,
      last_name: contactLastName,
    },

    account: {
      heroku_external_id__c: herokuAccountId,
    },
    contact: {
      heroku_external_id__c: membershipContactId,
      email: contactEmail,
    },
    membership__c: {
      heroku_external_id__c: herokuMembershipId,
      membership_contact__r__heroku_external_id__c: membershipContactId,
    },

    consent: {
      source: CONSENT_SOURCE.WEBSITE,
      membership_agreement: true,
      subscription: billing.autoRenewalConsent,
    },
  }

  // Stripe Product IDs
  if (selectedMembershipType && membershipProducts?.[selectedMembershipType]) {
    payload.membershipStripeProductId =
      membershipProducts[selectedMembershipType].stripe_id__c
  }

  if (selectedTrailerCareType && selectedTrailerCareType !== 'Do not upgrade') {
    payload.trailerCareType = selectedTrailerCareType

    if (trailerCareProducts?.[selectedTrailerCareType]) {
      payload.trailerCareStripeProductId =
        trailerCareProducts[selectedTrailerCareType].stripe_id__c
    }
  }

  // Ad Tracking & Marketing Attribution
  const msclkid = LS.getMemberApplicationMSClickId()
  const adTracking = LS.getMemberApplicationAdTracking() ?? {}

  if (Object.values(adTracking).some(Boolean)) {
    const mappedAdTracking = mapKeys(adTracking, (_, key) => `${key}__c`)
    payload = { ...payload, ...mappedAdTracking }
  }

  if (msclkid) payload.msclkid__c = msclkid
  if (gclid) payload.gclid__c = gclid
  if (testVariant) payload.test_variant__c = testVariant

  // Referral Credits
  if (referredByMemberNumber) payload.referred_by = referredByMemberNumber

  const { referralId, fetchedReferrerMemberNumber } = referralInfo || {}
  const isReferralMatch = fetchedReferrerMemberNumber === referredByMemberNumber
  
  if (isReferralMatch && referralId != null) payload.referral_id = referralId
  if (referralCreditsUsed) payload.referral_credits_used = referralCreditsUsed

  // Discount / Promo Codes
  if (discountCodeDetails) {
    const key =
      discountCodeDetails.type === DISCOUNT_CODE_TYPE.ACTIVATION
        ? 'activation_code_id'
        : 'promo_code_id'
    payload[key] = discountCodeDetails.id
  }

  return payload
}

export default apiValuesForPurchase
